import { useState, useEffect, useMemo } from "react";

import TextInput from "@shared/v2/TextInput";
import { Search as SearchIcon } from "@shared/v2/Icomoon";
import ListingsAutocompleteResults from "@shared/ListingsAutocomplete/ListingsAutocompleteResults";
import { useAutocomplete } from "./useAutocomplete";

const Search = ({
  selectSavedSearch,
  setLocation,
  searchBarRef,
  savedSearches,
  deleteSearch,
  searchType,
  mlsIds,
}) => {
  const mappedSearches = useMemo(
    () => savedSearches.map((s) => ({ ...s, value: s.name, category: "Saved Searches" })),
    [savedSearches],
  );

  const [term, setTerm] = useState("");
  const [results, setResults] = useState(mappedSearches);
  const [isSearchSelected, setIsSearchSelected] = useState(false);
  const [childPosition, setChildPosition] = useState(0);
  const { autocompleteResults, hasSearchResults } = useAutocomplete(term, searchType, mlsIds);

  const onSelectAutocompleteResult = (location) => {
    // Clean up results and tracking for them. Only show saved searches in the future
    setChildPosition(0);
    setTerm("");
    setResults(mappedSearches);

    if (location.category === "Saved Searches") {
      selectSavedSearch(location);
      return;
    }

    setLocation({
      isAreaSearch: location.category !== "Street Address",
      isAddressSearch: location.category === "Street Address",
      areaId: location.areaId,
      addressId: location.addressId,
      name: location.name,
      location: {
        lat: location.latitude,
        lng: location.longitude,
      },
    });
  };

  const handleKeypress = (event) => {
    if (event.defaultPrevented) return;

    switch (event.key) {
      case "ArrowUp":
        setChildPosition(childPosition > 0 ? childPosition - 1 : 0);
        break;
      case "ArrowDown":
        setChildPosition(results.length - 1 > childPosition ? childPosition + 1 : childPosition);
        break;
      case "Enter":
        onSelectAutocompleteResult(results[childPosition]);
        break;
      default:
        return;
    }

    event.preventDefault();
  };

  useEffect(() => {
    setResults([
      ...mappedSearches.filter((ms) => matchByTermIgnoreCase(ms.value, term)),
      ...autocompleteResults,
    ]);
  }, [mappedSearches, autocompleteResults, term]);

  return (
    <div className="tw-relative">
      <div className="tw-flex tw-flex-row">
        <TextInput
          ref={searchBarRef}
          autoComplete="off"
          containerClassName="tw-w-full"
          placeholder="Search by address, city, neighborhood, county, or ZIP code"
          onChange={(e) => setTerm(e.target?.value || "")}
          value={term}
          onBlur={() => setIsSearchSelected(false)}
          onFocus={() => setIsSearchSelected(true)}
          onKeyDown={handleKeypress}
        />
        <SearchIcon className="tw-absolute tw-right-[10px] tw-top-[calc(50%-8px)] tw-text-gray-30" size="l" />
      </div>
      {isSearchSelected && (
        <AutocompleteResults
          term={term}
          results={results}
          onSelectAutocompleteResult={onSelectAutocompleteResult}
          childPosition={childPosition}
          setChildPosition={setChildPosition}
          deleteSearch={deleteSearch}
          hasSearchResults={hasSearchResults}
          searchType={searchType}
        />
      )}
    </div>
  );
};

export default Search;

const AutocompleteResults = ({
  results,
  onSelectAutocompleteResult,
  childPosition,
  setChildPosition,
  deleteSearch,
  hasSearchResults,
  searchType,
}) => {
  const isEmptyState = hasSearchResults && results?.length === 0;

  return isEmptyState ? (
    <div className="tw-z-1000 tw-border tw-border-gray-20 tw-absolute tw-mx-10px tw-bg-white tw-left-0 tw-right-0 tw-text-14px tw-text-gray-50 tw-shadow-[0_1px_5px_1px_rgba(0,0,0,0.15),0_10px_16px_0_rgba(0,0,0,0.2)]">
      <div className="tw-px-12px tw-py-6px">{makeEmptyText(searchType)}</div>
    </div>
  ) : (
    <ListingsAutocompleteResults
      searchResults={results}
      selectResult={onSelectAutocompleteResult}
      childPosition={childPosition}
      setChildPosition={setChildPosition}
      deleteSearch={deleteSearch}
    />
  );
};

const makeEmptyText = (type) =>
  `No results found for this search. If you’re searching for an address, it may not be ${ByTypeEmptyState[type]}. `;

const matchByTermIgnoreCase = (searchIn, target) =>
  searchIn.match(new RegExp(target || "", "gi"))?.length > 0;

const ByTypeEmptyState = {
  expireds: "an Expired property",
  fsbos: "a FSBO",
  preforeclosures: "in Pre-Foreclosure",
};
